import React from "react";
import { Box, Typography } from "@mui/material";
import { PALETTE as P, CARD, NUM, TYPE, SPACE } from "./shared/tokens";
import * as C from "./shared/content";
import {
  Card, CountUp, Delta, Fill, Legend, RankedBarList, BarSparkline, AreaChart, LoadingView, stats,
} from "./shared/primitives";
import CampaignTable from "../components/CampaignTable";

// A · Calm.
//
// The shipped information in a card grid: four metric tiles with an icon chip
// and a column sparkline, pipeline and activity side by side, then plan mix as
// a donut. It is the least disruptive of the three — the same page an admin
// already knows, with its edges resolved — which is the point of having it in
// the comparison.
const RADIUS = 54;
const CIRC = 2 * Math.PI * RADIUS;

const Donut = ({ segments, delay = 0.6 }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  const arcs = segments.map((s) => {
    const len = (s.value / total) * CIRC;
    const arc = { ...s, len, offset: -offset };
    offset += len;
    return arc;
  });

  return (
    <Box sx={{ position: "relative", width: 148, height: 148, flex: "none" }}>
      <svg
        width="148"
        height="148"
        viewBox="0 0 148 148"
        role="img"
        aria-label={segments.map((s) => `${s.label} ${s.value}`).join(", ")}
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle cx="74" cy="74" r={RADIUS} fill="none" stroke={P.track} strokeWidth="18" />
        {arcs
          .filter((a) => a.len > 0)
          .map((a) => (
            <circle
              key={a.label}
              cx="74"
              cy="74"
              r={RADIUS}
              fill="none"
              stroke={a.color}
              strokeWidth="18"
              strokeDasharray={`${a.len.toFixed(1)} ${CIRC.toFixed(1)}`}
              strokeDashoffset={a.offset.toFixed(1)}
            />
          ))}
      </svg>
      {/* The donut is drawn, then uncovered — same reveal the charts use. */}
      <Box
        aria-hidden="true"
        className="dv-wipe"
        style={{ animationDelay: `${delay}s`, transformOrigin: "50% 50%" }}
        sx={{ position: "absolute", inset: "-2px", background: P.surface, borderRadius: "50%" }}
      />
    </Box>
  );
};

const DashboardA = ({ data, monetization, scope, isLoading }) => {
  const shell = {
    background: P.ground,
    borderRadius: "16px",
    // padding: { xs: SPACE.lg, md: SPACE.sm },
    display: "flex",
    flexDirection: "column",
    gap: SPACE.lg,
  };

  if (isLoading) {
    return (
      <Box sx={shell}>
        <LoadingView />
      </Box>
    );
  }

  const series = C.seriesFrom(data?.daily);
  const metrics = C.metrics(data, monetization);
  const pipe = C.pipeline(data);
  const plan = C.planMix(monetization);
  const assess = C.assessments(monetization);
  const comp = C.composition(data);
  const camp = C.campaigns(data);

  const planColours = { paid: P.accent, paid_earlier: P.pipeline.interested, free: P.pipeline.invited };
  const stageColour = {
    invited: P.pipeline.invited,
    interested: P.pipeline.interested,
    invested: P.pipeline.invested,
    rejected: P.pipeline.rejected,
  };

  return (
    <Box sx={shell}>

      <Box component="section" aria-label="Key metrics" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: SPACE.lg }}>
        {metrics.map((m, i) => (
          <Box key={m.key} className="dv-card" style={{ animationDelay: `${0.1 + i * 0.065}s` }} sx={{ ...CARD, display: "flex", flexDirection: "column", gap: SPACE.md }}>
            <Box display="flex" alignItems="center" gap={SPACE.md}>
              <Box sx={{ width: 30, height: 30, borderRadius: "9px", background: P.iconChip, color: P.accent, display: "grid", placeItems: "center", flex: "none" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d={m.icon} />
                </svg>
              </Box>
              <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft, fontWeight: 500 }}>{m.label}</Typography>
            </Box>

            <Box display="flex" alignItems="flex-end" justifyContent="space-between" gap={SPACE.md}>
              <Box display="flex" alignItems="baseline" gap={SPACE.md}>
                <CountUp value={m.value} sx={{ fontSize: "1.9rem", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.05, color: P.ink }} />
                <Delta value={m.delta} higherIsBetter={m.higherIsBetter} />
              </Box>
              {m.series && <BarSparkline values={series[m.series]} delay={0.45 + i * 0.05} />}
            </Box>

            <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{m.footnote}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(440px, 100%), 1fr))", gap: SPACE.lg, alignItems: "stretch" }}>
        <Card
          title="Pipeline"
          subtitle={pipe.subtitle}
          hint="Every invitation sent in this range, grouped by the status it holds right now. The four states are disjoint and add up to the total."
          delay={0.36}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
            {pipe.stages.map((s, i) => (
              <Box key={s.key} className="dv-item" style={{ animationDelay: `${0.55 + i * 0.04}s` }}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={SPACE.md} sx={{ marginBottom: "6px" }}>
                  <Typography sx={{ display: "inline-flex", alignItems: "center", gap: SPACE.sm, fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>
                    <Box component="span" sx={{ width: 10, height: 10, borderRadius: "3px", background: stageColour[s.key], flexShrink: 0 }} />
                    {s.label}
                  </Typography>
                  <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{s.n}</Typography>
                </Box>
                <Fill ratio={pipe.total ? s.n / pipe.total : 0} color={stageColour[s.key]} delay={0.6 + i * 0.06} height={10} />
              </Box>
            ))}
          </Box>

          <Box sx={{ marginTop: "auto", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: SPACE.sm }}>
            {pipe.rates.map((r, i) => (
              <Box key={r.label} className="dv-item" style={{ animationDelay: `${0.8 + i * 0.04}s` }} sx={{ padding: "12px 14px", borderRadius: "12px", background: P.accentSoft }}>
                <Typography sx={{ display: "block", fontSize: TYPE.micro, color: P.inkMuted }}>{r.label}</Typography>
                <Typography sx={{ ...NUM, fontSize: "1.1rem", fontWeight: 600, color: P.ink }}>{r.value}</Typography>
              </Box>
            ))}
          </Box>
        </Card>

        <Card title="Daily Activity" subtitle={C.activity.subtitle} hint="Two panels, one shared date axis, each scaled to its own series. Compare shapes between panels, not heights." delay={0.425}>
          {C.activity.panels.map((panel, i) => {
            const st = stats(series[panel.key]);
            return (
              <Box key={panel.key} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={SPACE.sm}>
                  <Typography sx={{ display: "inline-flex", alignItems: "center", gap: SPACE.sm, fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>
                    <Box component="span" sx={{ width: 9, height: 9, borderRadius: "3px", background: P.series[i] }} />
                    {panel.name}
                  </Typography>
                  <Box display="inline-flex" gap={0.75}>
                    {[`total ${st.total}`, `peak ${st.peak}`].map((chip) => (
                      <Typography key={chip} component="span" sx={{ ...NUM, padding: "2px 8px", borderRadius: "6px", background: P.track, fontSize: TYPE.micro, color: P.inkMuted }}>
                        {chip}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <AreaChart values={series[panel.key]} color={P.series[i]} label={`${panel.name}: total ${st.total}, peak ${st.peak}`} delay={0.55 + i * 0.1} gid={`a-${panel.key}`} />
              </Box>
            );
          })}
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(440px, 100%), 1fr))", gap: SPACE.lg, alignItems: "stretch" }}>
        <Card
          title="Plan Mix"
          subtitle={plan.subtitle}
          hint="A startup with no plan is a free (lite) user; one that bought a plan inside the range is paid. Anyone already paying before it is counted separately so the figures still add up to every startup."
          delay={0.49}
          right={<Legend keys={plan.legend.map((l) => ({ label: l.label, color: planColours[l.key] }))} />}
        >
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={SPACE.xl}>
            <Donut segments={plan.legend.map((l) => ({ label: l.label, value: l.value, color: planColours[l.key] }))} />
            <Box sx={{ flex: 1, minWidth: 200, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: SPACE.sm }}>
              {plan.legend.map((l) => (
                <Box key={l.key}>
                  <Typography sx={{ display: "block", fontSize: TYPE.micro, color: P.inkMuted }}>{l.label}</Typography>
                  <CountUp value={l.value} sx={{ fontSize: "1.35rem", fontWeight: 600, color: P.ink }} />
                </Box>
              ))}
              <Typography sx={{ gridColumn: "1 / -1", fontSize: TYPE.micro, color: P.inkMuted }}>{plan.footnote}</Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: `1px solid ${P.line}`, paddingTop: SPACE.md, display: "flex", flexDirection: "column", gap: SPACE.md }}>
            {plan.plans.map((p, i) => {
              const max = plan.plans.reduce((m, x) => Math.max(m, x.count), 0);
              return (
                <Box key={p.label} className="dv-item" style={{ animationDelay: `${0.75 + i * 0.04}s` }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: "6px" }}>
                    <Typography sx={{ display: "inline-flex", alignItems: "center", gap: SPACE.sm, fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>
                      {p.label}
                      {p.show_version && (
                        <Box component="span" sx={{ fontSize: "11px", padding: "0 6px", borderRadius: "5px", background: P.iconChip, color: P.accent, lineHeight: "18px" }}>
                          V{p.version}
                        </Box>
                      )}
                    </Typography>
                    <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{p.count}</Typography>
                  </Box>
                  <Fill ratio={max ? p.count / max : 0} color={P.pipeline.interested} delay={0.8 + i * 0.06} />
                </Box>
              );
            })}
            {plan.plans.length === 0 && (
              <Typography sx={{ fontSize: TYPE.label, color: P.inkMuted }}>No active subscriptions yet.</Typography>
            )}
          </Box>
        </Card>

        <Card
          title="Assessments Taken"
          subtitle={assess.subtitle}
          hint="Fundability and pitch-deck reports generated in this range, split by whether the startup unlocked the full result."
          delay={0.555}
          right={<Legend keys={[{ label: "Paid", color: P.accent }, { label: "Free", color: P.pipeline.invited }]} />}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))", gap: SPACE.md }}>
            {assess.rows.map((r, i) => (
              <Box
                key={r.key}
                className="dv-item"
                style={{ animationDelay: `${0.75 + i * 0.04}s` }}
                sx={{ borderRadius: "12px", background: P.ground, border: `1px solid ${P.line}`, padding: "16px", display: "flex", flexDirection: "column", gap: SPACE.sm }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                  <Typography sx={{ fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>{r.label}</Typography>
                  <CountUp value={r.total} sx={{ fontSize: "1.3rem", fontWeight: 600, color: P.ink }} />
                </Box>
                <Fill ratio={r.total ? r.paid / r.total : 0} color={P.accent} delay={0.85 + i * 0.05} />
                <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{r.footnote}</Typography>
              </Box>
            ))}
            {assess.rows.length === 0 && (
              <Typography sx={{ fontSize: TYPE.label, color: P.inkMuted }}>No assessments in this range.</Typography>
            )}
          </Box>
        </Card>
      </Box>

      <Box className="dv-fade" style={{ animationDelay: ".62s" }} component="section" sx={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
        <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={SPACE.sm}>
          <Typography component="h2" sx={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: P.ink }}>Composition</Typography>
          <Typography sx={{ fontSize: TYPE.meta, color: P.inkMuted }}>{comp.summary}</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: SPACE.lg }}>
          {comp.groups.map((g, i) => (
            <Box key={g.key} className="dv-card" style={{ animationDelay: `${0.66 + i * 0.065}s` }} sx={{ ...CARD, display: "flex", flexDirection: "column", gap: SPACE.sm }}>
              <Typography component="h3" sx={{ margin: 0, fontSize: TYPE.section, fontWeight: 600, color: P.ink }}>{g.title}</Typography>
              <RankedBarList rows={g.rows} footer={g.footer} delay={0.9 + i * 0.05} expandable={g.expandable} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="dv-fade" style={{ animationDelay: ".8s" }} component="section" sx={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
        <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={SPACE.sm}>
          <Typography component="h2" sx={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: P.ink }}>Campaign Attribution</Typography>
          <Typography sx={{ fontSize: TYPE.meta, color: P.inkMuted }}>{camp.summary}</Typography>
        </Box>
        {/* The shared admin listing, not a fourth hand-rolled table. A variant
            is allowed to change layout and motion, not to maintain its own
            table chrome — header styles, empty state and the mobile card
            fallback all come from the one component every other screen uses. */}
        <Box className="dv-card" style={{ animationDelay: ".85s" }}>
          <CampaignTable rows={camp.rows} />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardA;
